import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";

const HERO = "/hero-auth.jpg";

function AuthShell({ children, title, subtitle }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Entrepreneure africaine" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/55" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div>
            <div className="font-display text-2xl font-extrabold tracking-tight">NATOR<span className="text-primary">IQ</span></div>
            <div className="text-[11px] uppercase tracking-wider text-white/70 mt-1">Business Operating System</div>
          </div>
          <div>
            <h2 className="font-display text-4xl font-bold leading-tight">Pilotez toute votre entreprise. En un seul endroit.</h2>
            <p className="mt-4 text-white/80 max-w-md">Ventes, finances, stocks, RH, paie et Mobile Money réunis dans une plateforme conçue pour les entreprises africaines.</p>
            <span className="inline-block mt-5 text-xs font-semibold bg-primary/90 text-white rounded-full px-3 py-1.5">Built for African businesses</span>
          </div>
          <p className="text-white/60 text-sm">by <span className="font-semibold text-white/90">NATHOR</span></p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <div className="font-display text-2xl font-extrabold">NATOR<span className="text-primary">IQ</span></div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Business Operating System · by NATHOR</div>
          </div>
          <h1 className="font-display text-3xl font-bold text-foreground">{title}</h1>
          <p className="text-muted-foreground mt-1 mb-8">{subtitle}</p>
          {children}
          <p className="mt-10 text-center text-xs text-muted-foreground" data-testid="auth-footer">Developed by <span className="font-semibold text-foreground">NATHOR TECHNOLOGIES</span></p>
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      toast.success("Bienvenue !");
      navigate(res.has_company ? "/dashboard" : "/onboarding");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Connexion" subtitle="Accédez à votre tableau de bord.">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" placeholder="vous@entreprise.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Mot de passe</Label>
          <div className="relative">
            <Input id="password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" placeholder="••••••••" className="pr-10" />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="toggle-password" aria-label="Afficher/masquer le mot de passe">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div className="text-right">
            <Link to="/login" className="text-xs text-muted-foreground hover:text-primary" data-testid="forgot-password-link" onClick={(e) => { e.preventDefault(); toast.info("Réinitialisation du mot de passe bientôt disponible."); }}>Mot de passe oublié ?</Link>
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full h-11 active:scale-95 transition-transform" data-testid="login-submit-button">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
        </Button>
        <p className="text-sm text-muted-foreground text-center">
          Pas encore de compte ? <Link to="/register" className="text-primary font-semibold hover:underline" data-testid="go-register-link">Créer un compte</Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Compte créé ! Configurons votre entreprise.");
      navigate("/onboarding");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Créer un compte" subtitle="Quelques secondes pour démarrer.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="space-y-2">
          <Label htmlFor="name">Nom complet</Label>
          <Input id="name" value={form.full_name} onChange={set("full_name")} required data-testid="register-name-input" placeholder="Awa Ouédraogo" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="register-email-input" placeholder="vous@entreprise.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Téléphone (optionnel)</Label>
          <Input id="phone" value={form.phone} onChange={set("phone")} data-testid="register-phone-input" placeholder="+226 70 00 00 00" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pw">Mot de passe</Label>
          <Input id="pw" type="password" value={form.password} onChange={set("password")} required data-testid="register-password-input" placeholder="Min 8, 1 chiffre, 1 majuscule" />
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full h-11 active:scale-95 transition-transform" data-testid="register-submit-button">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer mon compte"}
        </Button>
        <p className="text-sm text-muted-foreground text-center">
          Déjà inscrit ? <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="go-login-link">Se connecter</Link>
        </p>
      </form>
    </AuthShell>
  );
}
